import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertTriangle,
  Clock,
  CheckCircle2,
  MessageSquare,
  FileText,
  Eye,
  Timer,
  Send,
  Upload,
  Hash,
  Calendar,
  ListChecks,
  Link as LinkIcon,
  Mail,
  AlignLeft,
  Loader2,
  FolderKanban,
  ExternalLink,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import FileUploadField from "../components/FileUploadField";
import { useNavigate } from "react-router-dom";
import SetupChatButton from "../components/SetupChatButton";
import ChangeRequestDialog, { CHANGE_TYPE_UK } from "../components/client-portal/ChangeRequestDialog";
import { downloadClientDocument, DueChip, SummaryTiles, Hint } from "../components/client-portal/PaymentBits";
import ProjectPicker from "../components/client-portal/ProjectPicker";
import KIcon from "../components/client-portal/KIcon";
import { useClientProject, resolveScope } from "../components/client-portal/projectScope";
import { fmtDate, money } from "../components/client-portal/format";
import { tr } from "../components/client-portal/i18n";

const REQUEST_TYPE_UK = {
  info_request: tr("Потрібна інформація"),
  work_review: tr("Перегляд роботи"),
  approval_request: tr("Потрібне погодження"),
  scope_approval: tr("Погодження обсягу"),
};

const ClientMyRequestsPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState({ pending: [], history: [] });
  const [loading, setLoading] = useState(true);
  const [portal, setPortal] = useState(null);
  const [changeReq, setChangeReq] = useState(null);
  const navigate = useNavigate();
  // ?seg= lets the dashboard deep-link straight into one kind of request.
  const [segment, setSegment] = useState(() => new URLSearchParams(window.location.search).get("seg") || "all");
  const [view, setView] = useState("open");
  const [scope, setScope] = useClientProject();
  const [viewRequest, setViewRequest] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  
  // Fill form state
  const [templateFieldValues, setTemplateFieldValues] = useState({});
  const [responseComment, setResponseComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const FIELD_ICONS = {
    checkbox: CheckCircle2,
    text: FileText,
    number: Hash,
    dropdown: ListChecks,
    file: Upload,
    date: Calendar,
    textarea: AlignLeft,
    url: LinkIcon,
    email: Mail,
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await api.get("/client/portal");
      setRequests({
        pending: res.data.action_required || [],
        history: res.data.history || [],
      });
      setPortal(res.data);
    } catch (error) {
      toast.error(tr("Не вдалося завантажити запити"));
    } finally {
      setLoading(false);
    }
  };

  const openViewRequest = async (requestId) => {
    setViewLoading(true);
    setTemplateFieldValues({});
    setResponseComment("");
    try {
      const res = await api.get(`/client-requests/${requestId}`);
      setViewRequest(res.data);
    } catch (error) {
      toast.error(tr("Не вдалося завантажити деталі запиту"));
    } finally {
      setViewLoading(false);
    }
  };

  const closeDialog = () => {
    setViewRequest(null);
    setTemplateFieldValues({});
    setResponseComment("");
  };

  const updateFieldValue = (fieldId, value) => {
    setTemplateFieldValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const isValidUrl = (string) => {
    if (!string) return true;
    try {
      const urlToTest = string.startsWith("http") ? string : `https://${string}`;
      new URL(urlToTest);
      return /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(string);
    } catch {
      return false;
    }
  };

  const isValidEmail = (email) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateFields = () => {
    if (!viewRequest?.template_fields) return { valid: true, errors: [] };
    
    const errors = [];
    const fields = viewRequest.template_fields;
    
    for (const field of fields) {
      const value = templateFieldValues[field.id];
      const hasValue = value && (typeof value === "string" ? value.trim() : true);
      
      if (field.is_required && !hasValue) {
        errors.push(`${tr("Заповніть поле")} «${field.label}»`);
        continue;
      }

      if (hasValue) {
        if (field.field_type === "url" && !isValidUrl(value)) {
          errors.push(`«${field.label}» — ${tr("вкажіть коректне посилання")}`);
        }
        if (field.field_type === "email" && !isValidEmail(value)) {
          errors.push(`«${field.label}» — ${tr("вкажіть коректний email")}`);
        }
      }
    }
    
    return { valid: errors.length === 0, errors };
  };

  const handleSubmitResponse = async (responseType) => {
    const { valid, errors } = validateFields();
    if (!valid) {
      errors.forEach(err => toast.error(err));
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/client-requests/${viewRequest.id}/respond`, {
        response_type: responseType,
        comment: responseComment,
        files: [],
        template_data: Object.keys(templateFieldValues).length > 0 ? templateFieldValues : null,
      });
      toast.success(tr("Відповідь надіслано!"));
      closeDialog();
      fetchRequests();
    } catch (error) {
      toast.error(tr("Не вдалося надіслати відповідь"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // ── One inbox for everything that waits on the client ─────────────────────
  // Sources (they used to live on different pages, which confused clients):
  //   • invoiced-but-unpaid payments (not the "payment_request" demands — those
  //     duplicate the invoice itself),  • work versions to approve,
  //   • budget / deadline / scope changes,  • our questions (template requests),
  //   • the project brief (setup chat).
  const p = portal || {};
  const items = [];
  (p.payments || [])
    .filter((x) => x.state === "overdue" || x.state === "invoiced")
    .forEach((x) => items.push({
      key: `pay-${x.id}`, seg: "pay", pid: x.project_id, done: false, overdue: x.state === "overdue",
      title: x.base_name || x.name,
      // Which part this is — the same wording as the payments page.
      sub: x.part_total > 1
        ? `${x.part_index}/${x.part_total} · ${x.part_index === 1 ? tr("Передоплата") : x.part_index === x.part_total ? tr("Фінальний") : tr("Проміжний")}${x.part_percent ? ` ${x.part_percent}%` : ""}`
        : tr("Повна оплата 100%"),
      project: x.project_name,
      meta: x.state === "overdue"
        ? `${tr("Прострочено · було до")} ${fmtDate(x.invoice_due_date) || "—"}`
        : `${tr("Оплатити до")} ${fmtDate(x.invoice_due_date) || "—"}`,
      amount: money(x.amount, x.currency),
      due: { kind: x.state === "overdue" ? "overdue" : "due", date: x.invoice_due_date },
      actions: [
        x.stripe_url && { label: tr("Оплатити карткою"), href: x.stripe_url },
        { label: tr("Рахунок"), primary: true, disabled: x.doc_ready === false, onClick: () => downloadClientDocument(x.id, "invoice") },
      ].filter(Boolean),
    }));
  (p.tasks_awaiting_approval || []).forEach((t) => items.push({
    key: `apr-${t.task_id}`, seg: "approve", pid: t.project_id, done: false, overdue: t.is_overdue,
    title: t.task_name, sub: t.version_number ? `${tr("версія")} ${t.version_number}` : null,
    project: t.project_name,
    meta: t.is_overdue ? `${tr("Прострочено · було до")} ${fmtDate(t.deadline) || "—"}` : `${tr("Відповісти до")} ${fmtDate(t.deadline) || "—"}`,
    due: { kind: t.is_overdue ? "overdue" : "due", date: t.deadline },
    actions: [{ label: tr("Переглянути"), primary: true, onClick: () => navigate(`/client/tasks/${t.task_id}`) }],
  }));
  (p.task_change_requests || []).forEach((r) => items.push({
    key: `chg-${r.id}`, seg: "changes", pid: r.project_id, done: false, overdue: false,
    title: CHANGE_TYPE_UK[r.change_type] || tr("Зміни"), sub: r.task_name, project: r.project_name,
    meta: r.created_at ? `${tr("Запит від")} ${fmtDate(r.created_at)}` : null,
    actions: [{ label: tr("Розглянути"), primary: true, onClick: () => setChangeReq(r) }],
  }));
  const isInfo = (r) => r.request_type !== "payment_request";
  (p.action_required || []).filter(isInfo).forEach((r) => items.push({
    key: `req-${r.id}`, seg: "info", pid: r.project_id, done: false, overdue: r.is_overdue,
    title: r.title, sub: REQUEST_TYPE_UK[r.request_type] || null, project: r.project_name,
    meta: r.is_overdue ? `${tr("Прострочено · було до")} ${fmtDate(r.deadline) || "—"}` : `${tr("Відповісти до")} ${fmtDate(r.deadline) || "—"}`,
    due: { kind: r.is_overdue ? "overdue" : "due", date: r.deadline },
    actions: [{ label: r.template_info?.fields?.length ? tr("Заповнити") : tr("Відповісти"), primary: true, onClick: () => openViewRequest(r.id) }],
  }));
  (p.history || []).filter(isInfo).forEach((r) => items.push({
    key: `reqh-${r.id}`, seg: "info", pid: r.project_id, done: true,
    title: r.title, sub: REQUEST_TYPE_UK[r.request_type] || null, project: r.project_name,
    meta: r.responded_at ? `${tr("Відповіли")} ${fmtDate(r.responded_at)}` : tr("Відповіли"),
    due: r.responded_at ? { kind: "done", date: r.responded_at } : null,
    actions: [{ label: tr("Переглянути"), onClick: () => openViewRequest(r.id) }],
  }));
  (p.setup_tasks || []).forEach((t) => items.push({
    key: `brief-${t.id}`, seg: "brief", pid: t.project_id, done: t.setup_stage === "completed", overdue: false,
    title: tr("Бриф проєкту"), sub: t.name, project: t.project_name,
    meta: t.setup_stage === "completed" ? tr("Заповнено") : tr("Потрібно заповнити"),
    custom: <SetupChatButton taskId={t.id} serviceName={t.name} serviceType={t.service_type} canEdit variant="button" />,
  }));

  const SEGMENTS = [
    { key: "all", label: tr("Усі") },
    { key: "pay", label: tr("Оплата") },
    { key: "approve", label: tr("Погодження робіт") },
    { key: "changes", label: tr("Зміни") },
    { key: "info", label: tr("Запити") },
    { key: "brief", label: tr("Бриф") },
  ];
  // Same project scope as the overview/payments/tasks (shared store).
  const projects = p.projects || [];
  const sel = resolveScope(scope, projects);
  const scoped = sel === "all" ? items : items.filter((i) => i.pid === sel);
  const openCount = (seg) => scoped.filter((i) => !i.done && (seg === "all" || i.seg === seg)).length;
  const shown = scoped
    .filter((i) => (segment === "all" || i.seg === segment) && i.done === (view === "done"))
    .sort((a, b) => (b.overdue ? 1 : 0) - (a.overdue ? 1 : 0));
  // Grouped by project, most urgent project first.
  const groups = [];
  shown.forEach((i) => {
    let g = groups.find((x) => x.pid === i.pid);
    if (!g) { g = { pid: i.pid, name: i.project || tr("Без проєкту"), rows: [] }; groups.push(g); }
    g.rows.push(i);
  });
  const SEG_ICON = { pay: "wallet", approve: "check", changes: "file", info: "chat", brief: "folder" };
  const ALL = { id: "all", name: tr("Усі проєкти"), status: null };
  const SEG_LABEL = Object.fromEntries(SEGMENTS.map((s) => [s.key, s.label]));

  return (
    <div className="space-y-5 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{tr("Кабінет клієнта")}</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-50 font-['Manrope'] tracking-tight mt-1">{tr("Мої запити")}</h1>
        </div>
        {projects.length > 1 && (
          <ProjectPicker
            projects={[ALL, ...projects]}
            selected={sel === "all" ? ALL : projects.find((x) => x.id === sel)}
            onSelect={setScope}
            flagOf={(pr) => (pr.id !== "all" && items.some((i) => !i.done && i.pid === pr.id && i.overdue) ? "bg-red-500"
              : pr.id !== "all" && items.some((i) => !i.done && i.pid === pr.id) ? "bg-amber-500" : null)}
          />
        )}
      </div>

      <SummaryTiles
        tiles={[
          { label: tr("Прострочено"), value: String(scoped.filter((i) => !i.done && i.overdue).length), tone: scoped.some((i) => !i.done && i.overdue) ? "overdue" : "muted" },
          { label: tr("Чекають вашої відповіді"), value: String(scoped.filter((i) => !i.done && !i.overdue).length), tone: "due" },
          { label: tr("Виконано"), value: String(scoped.filter((i) => i.done).length), tone: "paid" },
          { label: tr("Усього"), value: String(scoped.length) },
        ]}
      />

      {/* Segments + status — one bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-xl border border-zinc-800 bg-zinc-900/60 p-1">
          {SEGMENTS.map((s) => {
            const n = openCount(s.key);
            if (s.key !== "all" && n === 0 && !items.some((i) => i.seg === s.key)) return null;
            return (
              <button key={s.key} type="button" onClick={() => setSegment(s.key)}
                className={cn("inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
                  segment === s.key ? "bg-zinc-800 text-zinc-50 font-semibold" : "text-zinc-400 hover:text-zinc-100")}>
                {s.label}
                {n > 0 && <span className="rounded-md bg-orange-500/15 px-1.5 text-[11px] font-bold text-orange-500">{n}</span>}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex gap-1 rounded-xl border border-zinc-800 bg-zinc-900/60 p-1">
          {[["open", tr("Потрібна дія")], ["done", tr("Виконано")]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setView(k)}
              className={cn("rounded-lg px-3 py-1.5 text-sm transition-colors",
                view === k ? "bg-zinc-800 text-zinc-50 font-semibold" : "text-zinc-400 hover:text-zinc-100")}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800 rounded-2xl">
        <CardContent className="p-2">
          {shown.length === 0 ? (
            <div className="py-10 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-zinc-300">{view === "open" ? tr("Від вас зараз нічого не потрібно") : tr("Тут поки порожньо")}</p>
            </div>
          ) : groups.map((g) => (
            <div key={g.pid || "none"} className="pb-1">
              {sel === "all" && (
                <p className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">{g.name}</p>
              )}
              {g.rows.map((i) => (
            <div key={i.key}
              className="grid grid-cols-[8px_minmax(0,1fr)_auto] md:grid-cols-[8px_minmax(0,1.6fr)_140px_minmax(0,1fr)_120px_auto] items-center gap-x-4 gap-y-1 px-3 py-2.5 rounded-lg hover:bg-zinc-800/40">
              <span className={cn("w-2 h-2 rounded-full", i.done ? "bg-green-500" : i.overdue ? "bg-red-500" : "bg-orange-500")} />
              <div className="min-w-0">
                <p className="text-sm text-zinc-100 font-medium truncate" title={i.title}>
                  {i.title}
                  {i.sub && <span className="font-normal text-zinc-500"> · {i.sub}</span>}
                </p>
              </div>
              <Hint text={SEG_LABEL[i.seg]}>
                <span className="hidden md:inline-flex justify-self-start items-center gap-1.5 rounded-md border border-zinc-800 px-2 py-0.5 text-[11px] text-zinc-400 whitespace-nowrap">
                  <KIcon name={SEG_ICON[i.seg] || "box"} className="i" />
                  {SEG_LABEL[i.seg]}
                </span>
              </Hint>
              <div className={cn("hidden md:block min-w-0 text-xs truncate", i.done ? "text-zinc-500" : i.overdue ? "text-red-500" : "text-zinc-400")}>
                {i.due ? <DueChip {...i.due} tip={i.meta} /> : i.meta}
              </div>
              <p className="hidden md:block text-sm font-semibold text-zinc-100 tabular-nums text-right whitespace-nowrap">{i.amount || ""}</p>
              <div className="col-start-3 row-start-1 row-span-2 md:row-span-1 md:col-start-auto md:row-start-auto flex items-center justify-end gap-1.5">
                {i.custom || i.actions.map((a) => a.href ? (
                  <a key={a.label} href={a.href} target="_blank" rel="noopener noreferrer"
                    className="inline-flex h-8 items-center rounded-lg border border-orange-500/40 px-3 text-xs font-semibold text-orange-500 hover:bg-orange-500/10">
                    {a.label}
                  </a>
                ) : (
                  <Button key={a.label} size="sm" disabled={a.disabled} onClick={a.onClick}
                    title={a.disabled ? tr("Рахунок ще готує менеджер") : undefined}
                    className={cn("h-8 rounded-lg text-xs",
                      a.primary && !i.done ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100")}>
                    {a.label}
                  </Button>
                ))}
              </div>
            </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      <ChangeRequestDialog req={changeReq} onClose={() => setChangeReq(null)} onDone={fetchRequests} />

      {/* View Request Dialog */}
      <Dialog open={!!viewRequest} onOpenChange={closeDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Eye className="h-5 w-5 text-orange-400" />
              {viewRequest?.status === "pending" ? tr("Заповнити запит") : tr("Деталі запиту")}
            </DialogTitle>
          </DialogHeader>
          
          {viewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : viewRequest && (
            <div className="space-y-4 py-2">
              {/* Title and Status */}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-zinc-50">{viewRequest.title}</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={cn(
                    "text-xs",
                    viewRequest.status === "responded"
                      ? "bg-green-500/10 text-green-400 border-green-500/30"
                      : viewRequest.is_overdue
                        ? "bg-red-500/10 text-red-400 border-red-500/30"
                        : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                  )}>
                    {viewRequest.status === "responded" ? tr("✓ Відповіли") : viewRequest.is_overdue ? tr("ПРОСТРОЧЕНО") : tr("Очікує")}
                  </Badge>
                  {viewRequest.template_name && (
                    <Badge variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                      {viewRequest.template_name}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-800/50 rounded-lg text-sm">
                {viewRequest.project_name && (
                  <div>
                    <p className="text-xs text-zinc-500">{tr("Проєкт")}</p>
                    <p className="text-zinc-50">{viewRequest.project_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-zinc-500">{tr("Надіслано")}</p>
                  <p className="text-zinc-50">
                    {new Date(viewRequest.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Description */}
              {viewRequest.description && (
                <div className="space-y-1">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{tr("Опис запиту")}</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{viewRequest.description}</p>
                </div>
              )}

              {/* PENDING: Show Fill Form */}
              {viewRequest.status === "pending" && viewRequest.template_fields && viewRequest.template_fields.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">{tr("Заповніть необхідну інформацію")}</p>
                  <div className="space-y-3">
                    {viewRequest.template_fields.map((field) => {
                      const Icon = FIELD_ICONS[field.field_type] || FileText;
                      return (
                        <div key={field.id} className="space-y-1">
                          <Label className="text-zinc-400 text-sm flex items-center gap-2">
                            <Icon className="h-3.5 w-3.5" />
                            {field.label}
                            {field.is_required && <span className="text-red-400">*</span>}
                          </Label>
                          {field.field_type === "textarea" ? (
                            <Textarea
                              value={templateFieldValues[field.id] || ""}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                              placeholder={`${tr("Введіть")}: ${field.label.toLowerCase()}...`}
                              rows={3}
                            />
                          ) : field.field_type === "checkbox" ? (
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={templateFieldValues[field.id] || false}
                                onCheckedChange={(c) => updateFieldValue(field.id, c)}
                                className="border-zinc-600"
                              />
                              <span className="text-sm text-zinc-400">{tr("Так")}</span>
                            </div>
                          ) : field.field_type === "dropdown" ? (
                            <Select 
                              value={templateFieldValues[field.id] || ""} 
                              onValueChange={(v) => updateFieldValue(field.id, v)}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                                <SelectValue placeholder={`${tr("Оберіть")}: ${field.label.toLowerCase()}`} />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                {(field.options || []).map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : field.field_type === "file" ? (
                            <FileUploadField
                              value={templateFieldValues[field.id] || ""}
                              onChange={(url) => updateFieldValue(field.id, url)}
                              placeholder={tr("Завантажте файл або вставте посилання...")}
                            />
                          ) : (
                            <Input
                              type={field.field_type === "number" ? "number" : field.field_type === "email" ? "email" : field.field_type === "url" ? "url" : "text"}
                              value={templateFieldValues[field.id] || ""}
                              onChange={(e) => updateFieldValue(field.id, e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                              placeholder={
                                field.field_type === "url" ? "https://example.com" :
                                field.field_type === "email" ? "email@example.com" :
                                `${tr("Введіть")}: ${field.label.toLowerCase()}...`
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Comment */}
                  <div className="space-y-1 pt-2 border-t border-zinc-800">
                    <Label className="text-zinc-400 text-sm">{tr("Додатковий коментар (необовʼязково)")}</Label>
                    <Textarea
                      value={responseComment}
                      onChange={(e) => setResponseComment(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                      placeholder={tr("Будь-які додаткові примітки...")}
                      rows={2}
                    />
                  </div>
                </div>
              )}

              {/* PENDING without fields: Just comment */}
              {viewRequest.status === "pending" && (!viewRequest.template_fields || viewRequest.template_fields.length === 0) && (
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-sm">{tr("Ваша відповідь")}</Label>
                  <Textarea
                    value={responseComment}
                    onChange={(e) => setResponseComment(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder={tr("Введіть вашу відповідь...")}
                    rows={3}
                  />
                </div>
              )}

              {/* RESPONDED: Show submitted data */}
              {viewRequest.status === "responded" && (
                <>
                  {viewRequest.response_comment && (
                    <div className="space-y-1 p-3 bg-sky-500/10 border border-sky-500/20 rounded-lg">
                      <p className="text-xs text-sky-400 uppercase tracking-wider">{tr("Ваш коментар до відповіді")}</p>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap">{viewRequest.response_comment}</p>
                    </div>
                  )}

                  {viewRequest.template_fields && viewRequest.template_fields.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">{tr("Ваші надіслані дані")}</p>
                      <div className="space-y-2 p-3 bg-zinc-800/30 rounded-lg border border-zinc-800">
                        {viewRequest.template_fields.map((field) => {
                          const Icon = FIELD_ICONS[field.field_type] || FileText;
                          const value = viewRequest.template_data?.[field.id];
                          const hasValue = value && (typeof value === "string" ? value.trim() !== "" : true);
                          
                          return (
                            <div key={field.id} className="py-2 px-2 bg-zinc-800/50 rounded">
                              <div className="flex items-center gap-2 mb-1">
                                <Icon className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
                                <span className="text-sm text-zinc-400 font-medium">{field.label}</span>
                              </div>
                              {hasValue ? (
                                <div className="mt-1 ml-5">
                                  {field.field_type === "file" ? (
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1">
                                      <Download className="h-3 w-3" /> {tr("Завантажити файл")}
                                    </a>
                                  ) : field.field_type === "url" ? (
                                    <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1 break-all">
                                      <ExternalLink className="h-3 w-3 shrink-0" /> {value}
                                    </a>
                                  ) : field.field_type === "checkbox" ? (
                                    <span className={cn("text-sm", value ? "text-green-400" : "text-zinc-500")}>{value ? tr("✓ Так") : tr("✗ Ні")}</span>
                                  ) : (
                                    <p className="text-zinc-50 text-sm">{value}</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-zinc-600 text-sm italic mt-1 ml-5">{tr("Не вказано")}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800">
            {viewRequest?.status === "pending" && viewRequest?.request_type === "scope_approval" && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => handleSubmitResponse("rejected")}
                  disabled={submitting}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {tr("Відхилити зміни")}
                </Button>
                <Button
                  onClick={() => handleSubmitResponse("approved")}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  {tr("Погодити зміни")}
                </Button>
              </>
            )}
            {viewRequest?.status === "pending" && viewRequest?.request_type !== "scope_approval" && (
              <Button 
                onClick={() => handleSubmitResponse("info_provided")}
                disabled={submitting}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {tr("Надіслати відповідь")}
              </Button>
            )}
            <Button variant="outline" onClick={closeDialog} className="border-zinc-700">
              {tr("Закрити")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientMyRequestsPage;
